import { UrlParam } from "../models/url-param.model";
import { useAuthStore } from "../store/auth.store";
import { getApiBaseUrl } from "../config/runtimeConfig";
import { UtilService } from "./util.service";

export class BaseService {
    private static readonly utils: UtilService = UtilService.GetInstance();

    private static async Request(method: string, segment: string, body?: object | FormData, options?: RequestInit, CallbackCatch?: (error: Error) => void, spinner: boolean = false): Promise<any> {
        try {
            if (spinner) {
                this.utils.beginBlockingLoad();
            }

            const auth = useAuthStore.getState().getAuth();

            const headers: Record<string, string> = {
                'accept': '*/*',
                'Authorization': `Bearer ${auth?.accessToken.accessToken}`
            };

            if (!(body instanceof FormData)) {
                headers['Content-Type'] = 'application/json';
            }

            const requestBody = body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined);

            const response = await fetch(getApiBaseUrl() + segment, {
                method,
                headers,
                body: requestBody,
                ...options
            });

            if (response.status === 401) {
                UtilService.Alert('Important!', 'info', 'Session Expired. Please Login');
                window.location.href = '/#/login';
                return;
            }

            if (response.status === 204) {
                return;
            }

            let data;
            const text = await response.text();

            if (text && text.trim()) {
                try {
                    data = JSON.parse(text);
                } catch (parseError) {
                    data = { message: text };
                }
            } else {
                data = {};
            }

            if (response.ok) {
                return data;
            }

            if (response.status === 404) {
                throw new Error("NOT FOUND");
            }

            const message = data.message || `HTTP error! Status: ${response.status}`;

            throw new Error(message);
        } catch (error: any) {
            (CallbackCatch ?? this.Catch).call(this, error as Error);
        } finally {
            if (spinner) {
                this.utils.endBlockingLoad();
            }
        }
    }

    public static async Blob(method: string, segment: string, body?: object, CallbackCatch?: (error: Error) => void, spinner: boolean = false): Promise<any> {
        try {
            if (spinner) {
                this.utils.beginBlockingLoad();
            }

            const auth = useAuthStore.getState().getAuth();

            const response = await fetch(getApiBaseUrl() + segment, {
                method,
                headers: {
                    'accept': '*/*',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth?.accessToken.accessToken}`
                },
                body: body ? JSON.stringify(body) : undefined
            });

            if (response.status === 204) {
                return;
            }

            let blob = await response.blob()

            if (response.ok) {
                return blob;
            }

            if (response.status === 401) {
                throw new Error("UNAUTHORIZED");
            }

            if (response.status === 404) {
                throw new Error("NOT FOUND");
            }

            let error;
            try {
                const errorText = await response.text();
                if (errorText && errorText.trim()) {
                    error = JSON.parse(errorText);
                } else {
                    error = {};
                }
            } catch (parseError) {
                error = {};
            }

            const message = error.message || `HTTP error! Status: ${response.status}`;

            throw new Error(message);
        } catch (error: any) {
            (CallbackCatch ?? this.Catch).call(this, error as Error);
        } finally {
            if (spinner) {
                this.utils.endBlockingLoad();
            }
        }
    }

    private static Catch(error: Error) {
        if (error?.message.includes("VALIDATION: ")) {
            throw error;
        }

        if (error?.message.includes("Unexpected end of JSON input")) {
            console.warn("JSON parsing warning:", error.message);
            return;
        }

        let message: string = "Internal Server Error";

        if (error?.message.includes("SHOW: ")) {
            message = error.message.replace("SHOW: ", "")
        }

        UtilService.Alert('Important!', 'info', message);
    }

    public static Get(segment: string, CallbackCatch?: (error: Error) => void, spinner?: boolean): Promise<any>;
    public static Get(segment: string, options?: RequestInit, CallbackCatch?: (error: Error) => void, spinner?: boolean): Promise<any>;
    public static Get(segment: string, arg2?: any, arg3?: any, arg4?: any): Promise<any> {
        if (typeof arg2 === "function" || typeof arg2 === "boolean") {
            return this.Request("GET", segment, undefined, undefined, arg2, arg3);
        } else {
            return this.Request("GET", segment, undefined, arg2, arg3, arg4);
        }
    }

    public static Post(segment: string, body: object | FormData, CallbackCatch?: (error: Error) => void, spinner?: boolean): Promise<any>;
    public static Post(segment: string, body: object | FormData, options?: RequestInit, CallbackCatch?: (error: Error) => void, spinner?: boolean): Promise<any>;
    public static Post(segment: string, body: object | FormData, arg2?: any, arg3?: any, arg4?: any): Promise<any> {
        if (typeof arg2 === "function") {
            return this.Request("POST", segment, body, undefined, arg2, arg3 ?? true);
        }
        if (typeof arg2 === "boolean") {
            return this.Request("POST", segment, body, undefined, undefined, arg2);
        }
        return this.Request("POST", segment, body, arg2, arg3, arg4 ?? true);
    }

    public static Put(segment: string, body: object): Promise<any> {
        return this.Request('PUT', segment, body, undefined, undefined, true);
    }

    public static Delete(segment: string): Promise<any> {
        return this.Request('DELETE', segment, undefined, undefined, undefined, true);
    }

    public static Patch(segment: string, body?: object): Promise<any> {
        return this.Request('PATCH', segment, body, undefined, undefined, true);
    }

    public static BuildUrl(subroute: string, ...params: UrlParam[]): string {
        let newUrl: string = getApiBaseUrl() + subroute;
        let firstParamAdded = false;

        params.forEach((param: UrlParam) => {
            if (param.value !== "") {
                if (!firstParamAdded) {
                    newUrl += `?${param.name}=${param.value}`;
                    firstParamAdded = true;
                } else {
                    newUrl += `&${param.name}=${param.value}`;
                }
            }
        });

        return newUrl;
    }
}